import { TbReload } from "react-icons/tb";
import Button from "../Button/Button";
import ModalComponent from "../Modal/ModalComponent";
import ProgressBar from "../ProgressBar/ProgressBar";
import "./BackupImportation.css";
import { clamp } from "../../utils/math";
import { useBackup } from "../../contexts/backup";
import React, { useEffect, useState } from "react";
import Input from "../Input/Input";
import List from "../List/List";
import { ImportableEntityType, ImportationField, ImportationHiddenFields, ImportationRemovedFields, ImportItemStatus } from "../../utils/dictionary";
import { isNumeric } from "../../utils/number";
import { LicenseUnitSelector } from "../LicenseUnitSelector/LicenseUnitSelector";
import { useUserStore } from "../../contexts/userStore";
import { TagType } from "../../enums/tagTypes";
import { TagSingleSelector } from "../TagSingleSelector/TagSingleSelector";
import ToggleButton from "../ToggleButton/ToggleButton";
import { NotificationManager } from "react-notifications";

export function BackupImportation(props) {
	const {
		error,
		backup,
		modalLoadingIsOpen,
		setModalLoadingIsOpen,
		importBackup,
		setIsOpen,
		modalIsOpen,
		itemsError,
		setItemsError
	} = useBackup();
	const [password, setPassword] = useState("");
	const { selectedCondominioObj } = useUserStore();
	const [targetUnitId, setTargetUnitId] = useState();
	const [defaultUnitTag, setDefaultUnitTag] = useState(null);
	const [isMirrorMode, setIsMirrorMode] = useState(null);
	const [replicationUnitIds, setReplicationUnitIds] = useState([]);

	useEffect(() => {
		if (!selectedCondominioObj) return;
		setTargetUnitId(selectedCondominioObj.id)
	}, [selectedCondominioObj]);

	function calculateProgress() {
		if (!backup) return 0;
		if (!isNumeric(backup.currentStage) || !isNumeric(backup.totalImportingStages)) return 0;
		return error ? 100 : clamp(0, 100, (backup.currentStage / backup.totalImportingStages) * 100);
	}

	async function handleConfirm(type) {
		if (!targetUnitId) {
			NotificationManager.error("", "Selecione a licença alvo", 5000);
			return;
		}

		if (!defaultUnitTag) {
			NotificationManager.error("", "Selecione uma tag padrão", 5000);
			return;
		}

		if (!password) {
			NotificationManager.error("", "Informe sua senha para prosseguir com a importação", 5000);
			return;
		}

		await importBackup(
			backup,
			targetUnitId,
			replicationUnitIds,
			password,
			isMirrorMode,
			defaultUnitTag.externalId
		);
		setItemsError([]);
		setIsOpen(false);
		setPassword(null);
	}

	const handleCancel = (type) => {
		setIsOpen(false);
		setPassword(null);
		setModalLoadingIsOpen(false);
		setDefaultUnitTag(null);
		setIsMirrorMode(null);
		setReplicationUnitIds([]);
	};

	function getColor() {
		if (error) return '#ff0b0b';
		if (itemsError?.length > 0) return '#ff6d0bff';
		return null;
	}

	function getMessage() {
		if (error) return error;
		if (itemsError?.length > 0) return 'Backup parcialmente importado';
		return "Backup importado com sucesso!";
	}

	let progress = calculateProgress();

	return (
		<>
			{modalLoadingIsOpen && (
				<ModalComponent
					modalIsOpen={modalLoadingIsOpen}
					modalText={"Importação"}
					handleConfirm={() => handleConfirm("loading")}
					handleCancel={() => handleCancel("loading")}
					hideButtons={true}
				>
					<h2>
						{progress === 100 ? getMessage() : `Importando dados... ${Math.round(progress ?? 0)}%`}
					</h2>
					<ProgressBar progressValue={progress ?? 0} color={getColor()} message={getMessage()} />
					<div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
						{error && (
							<Button
								icon={<TbReload size={16} />}
								className="button-blue"
								label="Tentar novamente"
								onClick={() => importBackup(backup)}
							/>
						)}
						{calculateProgress() === 100 && (
							<Button
								className="button-white"
								label="Fechar"
								onClick={() => handleCancel("password")}
							/>
						)}
					</div>
					{itemsError?.length > 0 && (
						<div className="list-container list-visibility list-import-errors">
							<h3>Divergências ocorridas:</h3>
							<List
								selectable={false}
								collumsObjIndex={[
									{ collumnLabel: "UUID Importação", index: "importExternalId", type: "text" },
									{ collumnLabel: "Data", index: "createdAt", type: "date" },
									{
										collumnLabel: "Motivo", index: "status", type: "text", custom: ({ row }) => {
											return ImportItemStatus[row.status];
										}
									},
									{
										collumnLabel: "Objeto", index: "entityType", type: "text", custom: ({ row }) => {
											return ImportableEntityType[row.entityType];
										}
									},
								]}
								expandableContent={({ row }) => {
									const duplicatedField = row.reason?.match(/unique constraint \"idx_unique_([^)]+)\"/)?.[1];
									return (
										<div className="import-item-data">
											{Object.entries(row.entityData)
												.filter(([field, value]) => {
													const globalDictKey = `${row.entityType}__${field}`;
													return value && !ImportationHiddenFields.includes(globalDictKey) && !ImportationRemovedFields.includes(globalDictKey);
												})
												.map(([field, value]) => {
													const globalDictKey = `${row.entityType}__${field}`;
													return (
														<div key={field} className={duplicatedField === field ? 'field-error' : ''}>
															<span>{ImportationField[globalDictKey] ?? field}</span>
															<span>
																<span>{value}</span>
																{duplicatedField === field && (
																	<span>(Dado conflitante: Já existe outro cadastro com essa informação)</span>
																)}
															</span>
														</div>
													);
												})}
										</div>
									);
								}}
								data={itemsError.map(itemError => ({
									...itemError,
									_expandable_: true,
								}))}
							/>
						</div>
					)}
				</ModalComponent>
			)}
			{modalIsOpen && (
				<ModalComponent
					modalIsOpen={modalIsOpen}
					modalText={"Tem certeza que deseja prosseguir?"}
					handleConfirm={() => handleConfirm("password")}
					handleCancel={() => handleCancel("password")}
				>
					<React.Fragment>
						<div className="text-center! mb-5!">
							<p className="delete-button-modal-text">
								Esta é uma operação que não pode ser desfeita!
							</p>
							<p className="delete-button-modal-text text-red-400">
								Todos os dados atuais da nuvem referente a licença alvo serão <b className="font-bold!">PERMANENTEMENTE</b> excluídos!
							</p>
							<p className="delete-button-modal-text">
								Dados do backup serão registrados na nuvem.
							</p>
						</div>
						<div className="flex flex-col gap-5 w-full">
							<LicenseUnitSelector
								label={`Licença alvo`}
								unitId={targetUnitId}
								setUnitId={id => {
									setTargetUnitId(id);
									setReplicationUnitIds(prev => prev?.filter(prevId => prevId !== id));
								}}
								type={props.formMode}
							/>
							<LicenseUnitSelector
								singleCheck={false}
								label={`Vincular usuários também em: `}
								unitIds={replicationUnitIds}
								setUnitIds={ids => {
									setReplicationUnitIds(ids?.filter(id => id !== targetUnitId));
								}}
								type={props.formMode}
							/>
							<TagSingleSelector
								label={`Tag de unidade padrão`}
								selectedTag={defaultUnitTag}
								onTagChange={_tag => {
									setDefaultUnitTag(_tag);
								}}
								type={TagType.UNIT}
								filters={{
									sendToDevices: true,
									linkableToUser: true
								}}
								disabled={props.type === 'consulta'}
							/>
							<ToggleButton
								label="Espelhar"
								defaultChecked={isMirrorMode}
								onChange={({ target }) => setIsMirrorMode(target.checked)}
								required={false}
								disabled={props.type === "consulta"}
								tooltipText={
									"Espelhar os dados do backup na cloud, se desabilitado, os dados serão importados em estado de criação em relação ao equipamento."
								}
								tooltipVisible={true}
							/>
							<Input
								name="password"
								id="password"
								labelText={`Para continuar, confirme sua senha:`}
								placeholder="Digite sua senha"
								value={password}
								type="password"
								autoComplete="off"
								onChange={({ target }) => setPassword(target.value)}
								required={true}
							/>
						</div>
					</React.Fragment>
				</ModalComponent>
			)}
		</>
	);
}